import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

interface St { running: boolean; done: number; total: number; failed: number; last_error: string; projects_missing: number; articles_missing: number }

export function CraftImagesBar({ onChange }: { onChange: () => void }) {
  const [st, setSt] = useState<St | null>(null);
  const load = useCallback(() => api.get("/admin/craft-images/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.running) return;
    const t = setInterval(() => { load(); onChange(); }, 10000);
    return () => clearInterval(t);
  }, [st?.running, load, onChange]);
  const start = async () => {
    try { await api.post("/admin/craft-images/generate-all"); toast.success("Making example photos. This takes a while; you can leave this page."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const [section, setSection] = useState("for-her");
  const redo = async () => {
    if (!window.confirm("Remake the example photos in this section in the new varied style? Your own uploaded photos are never replaced.")) return;
    try { const r = await api.post("/admin/craft-images/redo", { section }); toast.success(`${r.data.flagged} example photos will be remade. Press Make next 20 photos.`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const missing = (st?.projects_missing || 0) + (st?.articles_missing || 0);
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-dashed border-[#23324A]/30 bg-[#FDFBF7] p-4" data-testid="craft-images-bar">
      <div className="min-w-0 flex-1 text-sm text-[#23324A]">
        <p className="font-bold">Finished-craft photos</p>
        <p className="text-[#23324A]/70" data-testid="craft-images-status">
          {!st ? "Loading…" : st.running ? `Making example photos: ${st.done} of up to ${st.total} made`
            : `${st.projects_missing} projects and ${st.articles_missing} articles still need a photo. Photos are made 20 at a time so you can check them. Example photos are labeled on the site until you upload your own.`}
          {st?.last_error && !st.running ? ` · ${st.last_error}` : ""}
        </p>
      </div>
      <button className="btn-coral" disabled={!st || st.running || missing === 0} onClick={start} data-testid="craft-images-generate-btn"><ImageIcon className="h-4 w-4" />{st?.running ? "Working…" : "Make next 20 photos"}</button>
      <select className="rounded-full border border-[#23324A]/30 bg-white px-3 py-2 text-sm" value={section} onChange={(e) => setSection(e.target.value)} data-testid="craft-images-redo-section">
        {[["for-her", "Gifts for Her"], ["for-him", "Gifts for Him"], ["kids", "Little Hands"], ["for-kids", "Gifts for Kids"], ["teacher", "Teacher Gifts"], ["wrapping", "Gift Wrapping"], ["holiday", "Holiday & Seasonal"], ["household", "Upcycled Gifts"], ["budget", "Budget & Last-Minute"], ["birthday", "Birthday Gifts"], ["personalized", "Personalized Gifts"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <button className="btn-ghost" disabled={!st || st.running} onClick={redo} data-testid="craft-images-redo-btn">Remake example photos</button>
    </div>
  );
}
